import { Box, Button, Chip, Rating, Stack, Tooltip } from "@mui/material";
import AddHomeWorkOutlinedIcon from "@mui/icons-material/AddHomeWorkOutlined";
import {
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import type {
  InheritanceReuseCandidateRow,
  InheritanceReuseSign,
} from "@/features/inheritanceReuse/types";
import {
  SALE_RANK_MAX,
  SORTABLE_FIELDS,
} from "@/features/inheritanceReuse/utils/constants";
import {
  displayOrDash,
  formatWithUnit,
} from "@/features/inheritanceReuse/utils/formatCell";
import { convertBookTypeToJpn } from "@/utils/utilsAcquireBook";
import { type BookTypeEng } from "@/types/acquirebook";

const KNOWN_BOOK_TYPES = new Set<BookTypeEng>([
  "LAND",
  "BUILDING",
  "CONDOMINIUM",
  "ONE_BUILDING",
]);

/** 不動産種別コードを日本語表記へ（未知の値はそのまま表示）。 */
const displayRealEstateType = (value: string): string => {
  const upper = value?.toUpperCase?.() ?? "";
  return KNOWN_BOOK_TYPES.has(upper as BookTypeEng)
    ? convertBookTypeToJpn(upper as BookTypeEng)
    : value ?? "";
};

/** 文字列配列を「、」区切りで表示（空配列は "-"）。 */
const joinList = (list: string[] | undefined): string =>
  list && list.length > 0 ? list.join("、") : "-";

/** スコア根拠をツールチップ用テキストへ。 */
const signsToText = (signs: InheritanceReuseSign[] | undefined): string =>
  signs && signs.length > 0
    ? signs.map((s) => `${s.label}（+${s.points}）`).join("\n")
    : "";

/**
 * スコア根拠セル。落ち着いた小さめの outlined チップで列挙し、全体をツールチップで内訳表示。
 * 下線リンク風は用いない。
 */
const SignsCell: React.FC<{ signs: InheritanceReuseSign[] | undefined }> = ({
  signs,
}) => {
  if (!signs || signs.length === 0) return <span>-</span>;
  return (
    <Tooltip
      title={<Box sx={{ whiteSpace: "pre-line" }}>{signsToText(signs)}</Box>}
    >
      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ py: 0.5 }}>
        {signs.map((s) => (
          <Chip
            key={s.code}
            label={s.label}
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: 11,
              color: "text.secondary",
              borderColor: "divider",
            }}
          />
        ))}
      </Stack>
    </Tooltip>
  );
};

/** ordering に無い列は sortable:false（ソート表示だけ変わりAPIに送られない不整合を防ぐ）。 */
const isSortable = (field: string): boolean => SORTABLE_FIELDS.has(field);

/**
 * 相続後リユース候補テーブルの列定義を生成する。
 * 列順は意味的グルーピング（操作 → 売却期待度/スコア → 次相続スコア → 物件属性 → 所有者 → 日付/その他）。
 * サーバソート可能な列は BE ALLOWED_ORDERINGS の 4 列のみ。その他は sortable:false。
 * @param onAcquire 「リユース取得」押下時に呼ぶコールバック（所在・地番を引き継いで遷移する）。
 */
export const makeInheritanceReuseColumns = (
  onAcquire: (row: InheritanceReuseCandidateRow) => void
): Array<GridColDef<InheritanceReuseCandidateRow>> => [
  {
    field: "action",
    headerName: "操作",
    width: 160,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: "center",
    headerAlign: "center",
    renderCell: (
      params: GridRenderCellParams<unknown, InheritanceReuseCandidateRow>
    ) => (
      // 月額会員向けの特典アクション。折り返さず（nowrap）、小さめ王冠系アイコン＋ラベルで
      // 上質感を出す。primary ティールの contained をベースにホバーで軽く強調。
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<AddHomeWorkOutlinedIcon />}
        onClick={() => {
          onAcquire(params.row);
        }}
        sx={{
          whiteSpace: "nowrap",
          borderRadius: 1.5,
          px: 1.5,
          fontWeight: 600,
          textTransform: "none",
          boxShadow: "none",
          "& .MuiButton-startIcon": { mr: 0.5 },
          "&:hover": {
            backgroundColor: "dark.main",
            boxShadow: "0 2px 6px -1px rgba(3,96,121,0.4)",
          },
        }}
      >
        登記を取得
      </Button>
    ),
  },
  {
    field: "saleRank",
    headerName: "売却期待度",
    width: 140,
    sortable: false, // sale_rank は ordering 非対応。ソートは売却期待スコア列で行う。
    renderCell: (
      params: GridRenderCellParams<number | null, InheritanceReuseCandidateRow>
    ) =>
      params.value == null ? (
        <span>-</span>
      ) : (
        <Rating
          value={params.value}
          max={SALE_RANK_MAX}
          readOnly
          size="small"
        />
      ),
  },
  {
    field: "saleScore",
    headerName: "売却スコア",
    width: 110,
    type: "number",
    sortable: isSortable("saleScore"),
    valueFormatter: ({ value }) => displayOrDash(value as number | null),
  },
  {
    field: "inheritanceScore",
    headerName: "次相続スコア",
    width: 120,
    type: "number",
    sortable: isSortable("inheritanceScore"),
    valueFormatter: ({ value }) => displayOrDash(value as number | null),
  },
  {
    field: "realEstateType",
    headerName: "種別",
    width: 90,
    sortable: false,
    valueFormatter: ({ value }) => displayRealEstateType(value as string),
  },
  {
    field: "prefectureName",
    headerName: "都道府県",
    width: 100,
    sortable: false,
  },
  {
    field: "cityName",
    headerName: "市区町村",
    width: 120,
    sortable: false,
    valueFormatter: ({ value }) => displayOrDash(value as string | null),
  },
  {
    field: "location",
    headerName: "所在",
    width: 220,
    sortable: false,
  },
  {
    field: "chibanKaokuNumber",
    headerName: "地番・家屋番号",
    width: 140,
    sortable: false,
  },
  {
    field: "distanceCategoryLabel",
    headerName: "距離区分",
    width: 140,
    sortable: false,
    valueFormatter: ({ value }) => displayOrDash(value as string | null),
  },
  {
    field: "ownerCount",
    headerName: "所有者数",
    width: 90,
    type: "number",
    sortable: false,
    valueFormatter: ({ value }) => displayOrDash(value as number | null),
  },
  {
    field: "ownerNames",
    headerName: "所有者氏名",
    width: 200,
    sortable: false,
    valueGetter: (params) => joinList(params.row.ownerNames),
  },
  {
    field: "ownerAddresses",
    headerName: "所有者住所",
    width: 240,
    sortable: false,
    valueGetter: (params) => joinList(params.row.ownerAddresses),
  },
  {
    field: "shares",
    headerName: "持分",
    width: 130,
    sortable: false,
    valueGetter: (params) => joinList(params.row.shares),
  },
  {
    field: "saleSigns",
    headerName: "売却期待の根拠",
    width: 240,
    sortable: false,
    renderCell: (
      params: GridRenderCellParams<
        InheritanceReuseSign[],
        InheritanceReuseCandidateRow
      >
    ) => <SignsCell signs={params.value} />,
  },
  {
    field: "inheritanceSigns",
    headerName: "次相続の根拠",
    width: 240,
    sortable: false,
    renderCell: (
      params: GridRenderCellParams<
        InheritanceReuseSign[],
        InheritanceReuseCandidateRow
      >
    ) => <SignsCell signs={params.value} />,
  },
  {
    field: "acquiredDate",
    headerName: "取得日",
    width: 120,
    sortable: isSortable("acquiredDate"),
    valueFormatter: ({ value }) => displayOrDash(value as string | null),
  },
  {
    field: "inheritanceDate",
    headerName: "相続登記日",
    width: 120,
    sortable: isSortable("inheritanceDate"),
    valueFormatter: ({ value }) => displayOrDash(value as string | null),
  },
  {
    field: "monthsSinceInheritance",
    headerName: "相続後経過月",
    width: 120,
    type: "number",
    sortable: false,
    valueFormatter: ({ value }) => displayOrDash(value as number | null),
  },
  {
    field: "isOccupant",
    headerName: "居住",
    width: 80,
    type: "boolean",
    sortable: false,
  },
  {
    field: "emptyHouse",
    headerName: "空き家",
    width: 90,
    type: "boolean",
    sortable: false,
  },
  {
    field: "buildYear",
    headerName: "築年",
    width: 90,
    type: "number",
    sortable: false,
    valueFormatter: ({ value }) => displayOrDash(value as number | null),
  },
  {
    // 地積（㎡・土地のみ。建物/区分は null＝「-」）。
    field: "landArea",
    headerName: "地積",
    width: 120,
    type: "number",
    sortable: false,
    valueFormatter: ({ value }) => formatWithUnit(value as number | null, "㎡"),
  },
  {
    // 公示価格（円/㎡・土地のみ）。3桁区切り。
    field: "landPrice",
    headerName: "公示価格",
    width: 130,
    type: "number",
    sortable: false,
    valueFormatter: ({ value }) =>
      formatWithUnit(value as number | null, "円/㎡"),
  },
  {
    // 推定土地評価額（円・公示価格×推計地積・土地のみ）。3桁区切り。
    field: "estimatedLandValue",
    headerName: "推定土地評価額",
    width: 150,
    type: "number",
    sortable: false,
    valueFormatter: ({ value }) => formatWithUnit(value as number | null, "円"),
  },
  {
    // 用途地域（土地のみ）。
    field: "usePurpose",
    headerName: "用途地域",
    width: 150,
    sortable: false,
    valueFormatter: ({ value }) => displayOrDash(value as string | null),
  },
];
